import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { BufferGeometry, MathUtils } from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";
import GUI from "lil-gui";
import gsap from "gsap";
import particlesVertexShader from "./shaders/particles/vertex.glsl";
import flakesVertexShader from "./shaders/particles/vertexFlake.glsl";
import particlesFragmentShader from "./shaders/particles/fragment.glsl";

import imagesLoaded from "imagesloaded";

/**
 * Base
 */
// Debug
// const gui = new GUI({ width: 340 });
const debugObject = {};

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
  pixelRatio: Math.min(window.devicePixelRatio, 2),
};

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();

// Loaders
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("./draco/");
/**
 * Loaders
 */
let progressCircle = document.querySelector(".progress-circle");
let loadingHolder = document.querySelector(".loading-holder");
let simulatedProgress = 0;
const maxSimulatedProgress = 0.95; // Max progress to simulate (95%)
const progressIncrement = 0.01;
const intervalTime = 100;

// Update the progress circle based on the progress ratio
const updateProgressCircle = (progressRatio) => {
  const degrees = progressRatio * 360;
  progressCircle.style.background = `conic-gradient(white ${degrees}deg, rgb(78, 78, 78) 0deg)`;
};

// Simulate progress increment
const simulateProgress = () => {
  if (simulatedProgress < maxSimulatedProgress) {
    simulatedProgress += progressIncrement;
    updateProgressCircle(simulatedProgress);
  }
};

const interval = setInterval(simulateProgress, intervalTime); // Update every 100ms

// Preload Images
const preloadImages = (selector = "img") => {
  return new Promise((resolve) => {
    imagesLoaded(
      document.querySelectorAll(selector),
      { background: true },
      resolve
    );
  });
};

// Initialize LoadingManager
const loadingManager = new THREE.LoadingManager(
  // Loaded
  () => {
    checkAllLoaded();
  },

  // Progress
  (itemUrl, itemsLoaded, itemsTotal) => {
    simulatedProgress = itemsLoaded / itemsTotal;
    updateProgressCircle(simulatedProgress);
  }
);

let threeAssetsLoaded = false;
let imagesHasLoaded = false;

// Check when everything is loaded
const checkAllLoaded = () => {
  if (threeAssetsLoaded && imagesHasLoaded) {
    clearInterval(interval);
    setTimeout(() => {
      gsap.to(overlayMaterial.uniforms.uAlpha, { duration: 3, value: 0 });
      loadingHolder.classList.add("ended");
      progressCircle.style.background = "";
      document.querySelector(".main-content").classList.remove("hidden");
    }, 500);
  }
};

// Start loading process
Promise.all([
  new Promise((resolve) => {
    loadingManager.onLoad = () => {
      threeAssetsLoaded = true;
      resolve();
    };
  }),
  preloadImages().then(() => {
    imagesHasLoaded = true;
  }),
]).then(checkAllLoaded);

const gltfLoader = new GLTFLoader(loadingManager);
gltfLoader.setDRACOLoader(dracoLoader);

const particleData = [];

// Objects array to store loaded models
const objects = [];

const models = [
  { url: "./hat.glb" },
  { url: "./heart.glb" },
  { url: "./salt-shaker-redo-3.glb" },
];

const objectsDistance = 16;
const positions = [
  { x: 2, y: -objectsDistance * 0 },
  { x: -2, y: -objectsDistance * 1 },
  { x: 2, y: -objectsDistance * 2 },
];
let flakeIndex = [];
const loadModel = (url, index) => {
  return new Promise((resolve) => {
    gltfLoader.load(url, (gltf) => {
      const model = gltf.scene;
      const meshes = [];

      // Traverse through all the children of the model (meshes)
      let flake = null;
      model.traverse((child) => {
        //console.log(child);
        if (child.isMesh) {
          // Store the meshes and apply necessary transformations
          meshes.push(child);
        }
        if (child.name === "flake") {
          flake = child; // Store the flake object
        }
      });

      // If meshes are found, process each mesh
      if (meshes.length > 0) {
        const meshGeometries = meshes.map((mesh) => {
          mesh.geometry = mesh.geometry.clone();
          return mesh.geometry;
        });
        if (!flake) {
          flake = {
            position: new THREE.Vector3(
              -0.0520162433385849,
              1.3895920515060425,
              -0.4323943853378296
            ),
          }; // Prevents crash
        }

        const flakePositions = [];
        const numParticles = 450;
        const baseX = flake.position.x + 2;
        const baseY = flake.position.y - 3;
        const baseZ = flake.position.z + 4;

        for (let i = 0; i < numParticles; i++) {
          flakeIndex[i] = i;
          // Create more particles at the top, less at the bottom (random y distribution)
          const yOffset = -(Math.random() * 4); // Random distribution within a vertical range

          // More particles near the top, gradually less as it goes down
          const densityFactor = Math.max(
            0.1,
            (Math.random() * Math.pow(10 - Math.abs(yOffset), 1.5)) / 10
          ); // Random density factor

          // Horizontal spread increases with downward movement
          const spreadFactor = Math.abs(yOffset) * 0.3 + densityFactor * 0.2; // Spread is influenced by downward position and density

          const xOffset = (Math.random() * 2 - 1) * spreadFactor;
          const zOffset = (Math.random() * 2 - 1) * spreadFactor;

          const x = baseX + xOffset;
          const y = baseY + yOffset;
          const z = baseZ + zOffset;

          flakePositions.push(x, y, z);
        }

        const flakeGeometry = new THREE.BufferGeometry();
        flakeGeometry.setAttribute(
          "position",
          new THREE.BufferAttribute(new Float32Array(flakePositions), 3)
        );
        flakeGeometry.setAttribute(
          "flakeIndex",
          new THREE.BufferAttribute(new Float32Array(flakeIndex), 1) // Scalar attribute (1 component per vertex)
        );

        // Return the cloned geometries and positions based on the index
        resolve({
          model,
          geometries: meshGeometries,
          position: positions[index],
          flakeGeometry: flakeGeometry,
        });
      } else {
        resolve(null);
      }
    });
  });
};

//orange
const colorA = "#EC7C60";
//green
const colorB = "#25A598";
const particlesMaterial = new THREE.ShaderMaterial({
  vertexShader: particlesVertexShader,
  fragmentShader: particlesFragmentShader,
  uniforms: {
    uColorA: new THREE.Uniform(new THREE.Color(colorA)),
    uColorB: new THREE.Uniform(new THREE.Color(colorB)),
    uProgress: new THREE.Uniform(0),
    uSize: new THREE.Uniform(0.3),
    uResolution: new THREE.Uniform(
      new THREE.Vector2(
        sizes.width * Math.min(window.devicePixelRatio, 2),
        sizes.height * Math.min(window.devicePixelRatio, 2)
      )
    ),
  },
  blending: THREE.AdditiveBlending,
  depthWrite: false,
});
const flakesMaterial = new THREE.ShaderMaterial({
  vertexShader: flakesVertexShader,
  fragmentShader: particlesFragmentShader,
  uniforms: {
    time: { value: 0.0 },
    shakeStrength: { value: 0.05 },
    uColorA: new THREE.Uniform(new THREE.Color(colorA)),
    uColorB: new THREE.Uniform(new THREE.Color(colorB)),
    uProgress: new THREE.Uniform(0),
    uSize: new THREE.Uniform(0.3),
    uResolution: new THREE.Uniform(
      new THREE.Vector2(
        sizes.width * sizes.pixelRatio,
        sizes.height * sizes.pixelRatio
      )
    ),
  },
  blending: THREE.AdditiveBlending,
  depthWrite: false,
});

const createParticlesFromGeometry = (geometry, position, isFlake = false) => {
  if (!geometry) {
    console.error("No geometry found for particles.");
    return;
  }

  // Get position attribute
  const posAttr = geometry.attributes.position;
  const count = posAttr.count;
  const positions = new Float32Array(count * 3);

  // Extract vertex positions
  for (let i = 0; i < count; i++) {
    positions[i * 3] = posAttr.getX(i); // X
    positions[i * 3 + 1] = posAttr.getY(i); // Y
    positions[i * 3 + 2] = posAttr.getZ(i); // Z
  }

  // Create new particle geometry
  const particlesGeometry = new THREE.BufferGeometry();
  particlesGeometry.setAttribute(
    "position",
    new THREE.BufferAttribute(positions, 3)
  );
  let mesh;
  if (isFlake) {
    mesh = new THREE.Points(particlesGeometry, flakesMaterial);
  } else {
    // Create normal particles
    mesh = new THREE.Points(particlesGeometry, particlesMaterial);
  }
  mesh.position.set(position.x, position.y, position.z);
  scene.add(mesh);

  return mesh;

  // // Create the points object
  // const particles = new THREE.Points(particlesGeometry, particlesMaterial);
  // particles.position.set(position.x, position.y, position.z);
  // scene.add(particles);
  // return particles;
};

// Load all models and wait for completion
let flakesMesh;
Promise.all(models.map((model, index) => loadModel(model.url, index))).then(
  // loadedObjects is an array of resolved values from loadModel(...).
  (loadedObjects) => {
    //console.log(loadedObjects);
    const [hat, loveheart, saltshaker] = loadedObjects;

    if (hat) {
      hat.geometries.forEach((geometry) => {
        geometry.translate(3, -3, 0); // Translate the geometry
        createParticlesFromGeometry(geometry, hat.position);
      });
    }

    if (loveheart) {
      loveheart.geometries.forEach((geometry) => {
        geometry.scale(1.2, 1.2, 1.2); // Scale the geometry
        geometry.translate(0, 0, 6); // Translate the geometry
        geometry.rotateY(-Math.PI / 2); // Rotate the geometry
        createParticlesFromGeometry(geometry, loveheart.position);
      });
    }

    if (saltshaker) {
      //const model = saltshaker.model;
      saltshaker.geometries.forEach((geometry) => {
        geometry.translate(6, -6, 0);
        geometry.rotateY(THREE.MathUtils.degToRad(300));
        geometry.scale(0.85, 0.85, 0.85);
        //scene.add(model);
        createParticlesFromGeometry(geometry, saltshaker.position);
      });
      if (saltshaker.flakeGeometry) {
        flakesMesh = createParticlesFromGeometry(
          saltshaker.flakeGeometry,
          saltshaker.position,
          true
        );
        // if (flakesMesh && flakesMesh.position) {
        //   gsap.to(flakesMesh.position, { duration: 2, y: -33 });
        // }
      }
    }

    console.log("All models converted to particles!");
  }
);

/**
 * Cursor
 */
const cursor = {};
cursor.x = 0;
cursor.y = 0;

window.addEventListener("mousemove", (event) => {
  cursor.x = event.clientX / sizes.width - 0.5;
  cursor.y = event.clientY / sizes.height - 0.5;
});

let resizeTimer;
window.addEventListener("resize", () => {
  // Update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;
  sizes.pixelRatio = Math.min(window.devicePixelRatio, 2);

  // Update camera
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    updateCamera();
  }, 100);
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
//Group
const cameraGroup = new THREE.Group();
scene.add(cameraGroup);
// Base camera
const camera = new THREE.PerspectiveCamera(
  35,
  sizes.width / sizes.height,
  0.1,
  100
);

//orginal position 20
function updateCamera() {
  camera.aspect = window.innerWidth / window.innerHeight;
  if (window.innerWidth <= 576) {
    camera.position.z = 50;
  } else if (window.innerWidth <= 1024) {
    camera.position.z = 30;
  } else {
    camera.position.z = 20;
  }
  camera.updateProjectionMatrix();
}
updateCamera();

cameraGroup.add(camera);

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
  antialias: true,
  alpha: true,
});

renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

debugObject.clearColor = "#160920";
// gui.addColor(debugObject, "clearColor").onChange(() => {
//   renderer.setClearColor(debugObject.clearColor);
// });
//renderer.setClearColor(debugObject.clearColor);

/**
 * Overlay
 */
const overlayGeometry = new THREE.PlaneGeometry(2, 2, 1, 1);
const overlayMaterial = new THREE.ShaderMaterial({
  transparent: true,
  uniforms: {
    uAlpha: { value: 0.5 },
  },
  vertexShader: `
      void main()
      {
        gl_Position = vec4(position, 1.0);
      }
  `,
  fragmentShader: `
      uniform float uAlpha;
      void main()
      {
          gl_FragColor = vec4(0.0, 0.0, 0.0, uAlpha);
      }
  `,
});
const overlay = new THREE.Mesh(overlayGeometry, overlayMaterial);
// scene.add(overlay);

/**
 * Scroll
 */
let scrollY = window.scrollY;
window.addEventListener("scroll", () => {
  scrollY = window.scrollY;
});

/**
 * Animate
 */
const clock = new THREE.Clock();
let previousTime = 0;

const tick = () => {
  const time = clock.getElapsedTime();
  const deltaTime = time - previousTime;
  previousTime = time;
  if (flakesMesh) {
    flakesMesh.material.uniforms.time.value += 0.1;
  }

  const parallaxX = cursor.x;
  const parallaxY = -cursor.y;

  cameraGroup.position.x +=
    (parallaxX - cameraGroup.position.x) * 10 * deltaTime;
  cameraGroup.position.y +=
    (parallaxY - cameraGroup.position.y) * 10 * deltaTime;

  // Animate camera
  camera.position.y = (-scrollY / sizes.height) * objectsDistance;
  // Render normal scene
  renderer.render(scene, camera);

  // Call tick again on the next frame
  window.requestAnimationFrame(tick);
};

tick();
